import { includes } from 'lodash';
import { Record } from 'immutable';

import { DEFAULT_KEY_ORDER, CASH_PURCHASE_ONLY_DISCOUNT } from 'commons-vehicle/constants/dataConversions.js';
import { ITEM_FLAG, DEALER_OPTION, DATA_TYPES, DEALER_INCENTIVES } from 'commons-vehicle/constants/dataTypes.js';

const { FEATURE, FEE, TAX_AND_DUTY, REBATE } = DATA_TYPES;

const OptionRecord = Record({
    selectionCode: '',
    enabled: true,
    visible: true,
    selected: false,
    isConfigurationOption: false,
    dueAtDelivery: false,
    userSelected: false,
    code: '',
    keyOrder: DEFAULT_KEY_ORDER,
    parentCode: '',
    includedCodes: [],
    label: '',
    description: '',
    note: '',
    dataType: 0,
    typeID: 0,
    typeDescription: '',
    typeNote: '',
    parentTypeID: null,
    parentTypeDescription: '',
    parentTypeNote: '',
    categoryID: 0,
    supplier: '',
    supplierCodes: [],
    flag: 0,
    retail: 0,
    residualizingAmount: 0,
    isTaxable: true,
    imageUrl: '',
    webUrl: '',
    htmlBlock: '',
    mostPopular: false,
    isGroup: false,
    isInstalledAtDealership: false,
    protectionCoverage: null,
    isIncludedInPriceOrPayment: false,
});

class Option extends OptionRecord {
    getSelectionCode() {
        return this.get('selectionCode');
    }

    getShowroomQuoteItem() {
        const { dataType, typeID, code, supplier, flag, retail, residualizingAmount, selected, userSelected } = this;
        // Reset flag based on selected field
        let selectionFlag = flag;
        if (selected) {
            selectionFlag = flag | ITEM_FLAG.SELECTED | (userSelected ? ITEM_FLAG.USER_SELECTED : 0);
        } else {
            selectionFlag = flag & ~ITEM_FLAG.SELECTED;
        }

        return {
            dataType,
            typeID,
            code,
            supplier,
            flag: selectionFlag,
            amounts: {
                retail,
                residualizingAmount,
            },
        };
    }

    calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor) {
        const { selectionCode, retail, residualizingAmount, isTaxable } = this;

        let payment = 0;
        if (selectedFinanceTerm) {
            payment =
                Math.round(selectedFinanceTerm.calculatePaymentAdjustment(retail, residualizingAmount) * 100) / 100;
        }

        const itemAmounts = {
            selectionCode,
            retail,
            taxes: isTaxable ? Math.round(retail * taxesFactor * 100) / 100 : 0,
            payment,
            paymentTaxes: isLease || isTaxable ? Math.round(payment * taxesFactor * 100) / 100 : 0,
        };

        return itemAmounts;
    }

    getValidationOption(isCashPurchase) {
        const { retail, residualizingAmount, code, supplier, userSelected, dataType, typeID } = this;

        const isDealerDiscount = isCashPurchase && this.isDealerCashPurchaseOnlyDiscount();
        const selectionFlag =
            (this.selected && !this.isDealerCashPurchaseOnlyDiscount()) || isDealerDiscount
                ? ITEM_FLAG.SELECTED
                : ITEM_FLAG.NOT_SELECTED;

        return {
            code,
            supplier,
            dataType,
            typeID,
            flag: selectionFlag | (userSelected ? ITEM_FLAG.USER_SELECTED : 0),
            amounts: {
                retail,
                residualizingAmount,
            },
        };
    }

    getOptionDescriptionWithAmounts(isLease, selectedFinanceTerm, taxesFactor, isReduction) {
        const optionPricing = this.calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor, isReduction);

        if (isReduction) {
            optionPricing.retail *= -1;
            optionPricing.taxes *= -1;
            optionPricing.payment *= -1;
            optionPricing.paymentTaxes *= -1;
        }

        return {
            description: this.description,
            imageUrl: this.imageUrl,
            ...optionPricing,
        };
    }

    isDealerInventoryOption() {
        return (
            this.supplier === DEALER_OPTION &&
            this.dataType === FEATURE.DATA_TYPE_ID &&
            this.typeID === FEATURE.TYPES.DEALER_INVENTORY
        );
    }

    isDealerDiscount() {
        return (
            ((this.supplier === DEALER_OPTION && this.typeID === REBATE.TYPES.DEALER_DISCOUNT) ||
                this.supplier === DEALER_INCENTIVES) &&
            this.dataType === REBATE.DATA_TYPE_ID
        );
    }

    isDealerFee() {
        return (
            this.supplier === DEALER_OPTION &&
            (this.dataType === FEE.DATA_TYPE_ID || this.dataType === TAX_AND_DUTY.DATA_TYPE_ID)
        );
    }

    isDealerCashPurchaseOnlyDiscount() {
        return this.isDealerDiscount() && includes(this.supplierCodes, CASH_PURCHASE_ONLY_DISCOUNT);
    }

    isDealerIncentive() {
        return this.supplier === DEALER_INCENTIVES && this.dataType === REBATE.DATA_TYPE_ID;
    }
}

export default Option;
